// The words a person reads for each pricing setting. Keys are the database's.

export const SETTING_WORDS: Record<string, { label: string; unit: 'per_sf' | 'money' | 'pct' | 'number' | 'minutes' | 'flag' }> = {
  rate_per_sf_6_nights: { label: 'Rate per sqft, 6 nights', unit: 'per_sf' },
  rate_per_sf_5_nights: { label: 'Rate per sqft, 5 nights', unit: 'per_sf' },
  rate_per_sf_3_nights: { label: 'Rate per sqft, 3–4 nights', unit: 'per_sf' },
  small_site_floor_monthly_6_nights: { label: 'Small-site monthly floor, 6 nights', unit: 'money' },
  small_site_floor_monthly_3_nights: { label: 'Small-site monthly floor, 3–4 nights', unit: 'money' },
  sf_per_chair_estimate: { label: 'Sqft per chair (walkthrough pending)', unit: 'number' },
  sub_cost_share: { label: 'Sub cost as a share of the rate', unit: 'pct' },
  target_margin: { label: 'Target margin', unit: 'pct' },
  floor_margin: { label: 'Floor margin ("don\'t go below")', unit: 'pct' },
  sub_per_sf_sterile: { label: 'Sub cost per sterile sqft', unit: 'per_sf' },
  sub_per_sf_non_sterile: { label: 'Sub cost per non-sterile sqft', unit: 'per_sf' },
  sub_intercept_monthly: { label: 'Sub cost, fixed monthly part', unit: 'money' },
  price_per_sf_sterile: { label: 'Price fit, per sterile sqft', unit: 'per_sf' },
  price_per_sf_non_sterile: { label: 'Price fit, per non-sterile sqft', unit: 'per_sf' },
  price_intercept_monthly: { label: 'Price fit, fixed monthly part', unit: 'money' },
  minutes_per_or_terminal: { label: 'Minutes per OR terminal clean', unit: 'minutes' },
  minutes_per_bay: { label: 'Minutes per pre-op / PACU bay', unit: 'minutes' },
  sf_per_hour_sterile: { label: 'Sterile sqft cleaned per hour', unit: 'number' },
  sf_per_hour_non_sterile: { label: 'Non-sterile sqft cleaned per hour', unit: 'number' },
  sub_overhead_factor: { label: 'Sub overhead on labour (× hours × rate)', unit: 'number' },
  loaded_hourly_rate: { label: 'Loaded crew cost per hour', unit: 'money' },
  // floor care: one baseline per service in the app's floor-care list (bid step 3, #841)
  floor_care_strip_wax_per_sf: { label: 'Strip & wax, per sqft per visit', unit: 'per_sf' },
  floor_care_autoscrub_wax_per_sf: { label: 'Scrub & recoat (auto-scrub & wax), per sqft per visit', unit: 'per_sf' },
  floor_care_auto_scrub_per_sf: { label: 'Auto scrub, per sqft per visit', unit: 'per_sf' },
  floor_care_carpet_clean_per_sf: { label: 'Carpet clean, per sqft per visit', unit: 'per_sf' },
  // consumables on a bid: Ben, 2026-10-15
  consumables_pct_of_monthly_default: { label: 'Consumables "% of monthly": default', unit: 'pct' },
  consumables_pct_of_monthly_min: { label: 'Consumables "% of monthly": lowest allowed', unit: 'pct' },
  consumables_pct_of_monthly_max: { label: 'Consumables "% of monthly": highest allowed', unit: 'pct' },
  consumables_cost_plus_markup_default: { label: 'Consumables "cost + %": default markup', unit: 'pct' },
  consumables_cost_plus_markup_min: { label: 'Consumables "cost + %": lowest markup allowed', unit: 'pct' },
  consumables_cost_plus_markup_max: { label: 'Consumables "cost + %": highest markup allowed', unit: 'pct' },
  // the portfolio supply estimate; no longer shown on a bid (Ben, 2026-10-15)
  consumables_share_of_rate: { label: 'Consumables inside the rate (portfolio supply estimate)', unit: 'pct' },
  price_rounding: { label: 'Round prices to the nearest', unit: 'money' },
  kc_dialysis_floor_per_sf: { label: 'Kansas City floor per sqft (March 16)', unit: 'per_sf' },
  kc_dialysis_average_per_sf: { label: 'Kansas City average per sqft (March 16)', unit: 'per_sf' },
  kc_dialysis_starting_per_sf: { label: 'Kansas City starting price per sqft (March 16)', unit: 'per_sf' },
  exclude_from_comps: { label: 'Left out of the comparables', unit: 'flag' },
};

export function showValue(key: string, v: number | null): string {
  if (v == null) return 'cleared';
  const u = SETTING_WORDS[key]?.unit ?? 'number';
  if (u === 'per_sf') {
    // two decimals always, up to four when the rate has them: $1.00, $0.29, $0.3026
    const four = Number(v).toFixed(4);
    return `$${four.endsWith('00') ? Number(v).toFixed(2) : four.replace(/0$/, '')}`;
  }
  if (u === 'money') return `$${Number(v).toLocaleString('en-US')}`;
  if (u === 'pct') return `${(Number(v) * 100).toFixed(1).replace(/\.0$/, '')}%`;
  if (u === 'minutes') return `${v} min`;
  if (u === 'flag') return Number(v) === 1 ? 'Yes' : 'No';
  return Number(v).toLocaleString('en-US');
}

/** Values are typed as a person says them: 30 for 30%, 0.29 for $0.29. */
export function toStored(key: string, typed: number): number {
  return SETTING_WORDS[key]?.unit === 'pct' ? typed / 100 : typed;
}
export function toTyped(key: string, stored: number | null): string {
  if (stored == null) return '';
  return SETTING_WORDS[key]?.unit === 'pct' ? String(Math.round(Number(stored) * 10000) / 100) : String(stored);
}
